import { useCallback, useEffect, useState } from 'react';

/**
 * Google-only customers have no password; they set their first one with a 6-digit code
 * emailed to them (backend: POST /api/appUser/password/setup-code and /password/setup).
 */
export const PASSWORD_SETUP_CODE_LENGTH = 6;

const ERROR_KEYS = {
    PASSWORD_SETUP_CODE_INVALID: 'profilePage.setPassword.errors.invalidCode',
    PASSWORD_SETUP_CODE_EXPIRED: 'profilePage.setPassword.errors.expiredCode',
    PASSWORD_SETUP_CODE_TOO_MANY_ATTEMPTS: 'profilePage.setPassword.errors.tooManyAttempts',
    PASSWORD_SETUP_CODE_COOLDOWN: 'profilePage.setPassword.errors.cooldown',
    PASSWORD_ALREADY_SET: 'profilePage.setPassword.errors.alreadySet',
};

/** Error details the backend sends as `errors: { code, attemptsRemaining, retryAfterSeconds }`. */
export function getPasswordSetupErrorDetails(err) {
    const errors = err?.response?.data?.errors;
    return errors && !Array.isArray(errors) && typeof errors === 'object' ? errors : {};
}

/** Translated message for a password-setup API error, falling back to the server message. */
export function getPasswordSetupErrorMessage(err, t, fallbackKey) {
    const body = err?.response?.data || {};
    const details = getPasswordSetupErrorDetails(err);
    const key = ERROR_KEYS[details.code];

    if (key === ERROR_KEYS.PASSWORD_SETUP_CODE_INVALID && Number.isFinite(details.attemptsRemaining)) {
        return t('profilePage.setPassword.errors.invalidCodeAttempts', { count: details.attemptsRemaining });
    }
    if (key === ERROR_KEYS.PASSWORD_SETUP_CODE_COOLDOWN && Number.isFinite(details.retryAfterSeconds)) {
        return t(key, { count: details.retryAfterSeconds });
    }
    if (key) return t(key);
    if (Array.isArray(body.errors) && body.errors.length > 0) return String(body.errors[0]);
    return (typeof body.message === 'string' && body.message.trim()) || t(fallbackKey);
}

/** "Email me a code" step: sends the code and runs the resend countdown. */
export function usePasswordSetupCode(requestCode) {
    const [codeSent, setCodeSent] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    /** Resolves on success; rethrows so the caller can show the error its own way. */
    const sendCode = useCallback(async () => {
        setSending(true);
        try {
            const res = await requestCode();
            const data = res?.data?.data ?? res?.data ?? {};
            setMaskedEmail(data.maskedEmail || '');
            setCodeSent(true);
            setCooldown(Number(data.resendAfterSeconds) || 60);
        } catch (err) {
            const retry = Number(getPasswordSetupErrorDetails(err).retryAfterSeconds);
            if (Number.isFinite(retry) && retry > 0) setCooldown(retry);
            throw err;
        } finally {
            setSending(false);
        }
    }, [requestCode]);

    return { codeSent, maskedEmail, sending, cooldown, sendCode };
}
